import {
  CalculatorBodyText,
  CalculatorHeadline,
  CalculatorInputHeadline,
  H3Headline,
  ListLinkText,
} from "../CalculatorTypography";
import { DisplayResult, DisplayResultFirst, DisplayResultWithTwoText } from "./DisplayResult";
import { Jformula, Kformula, Mformula, Sformula, Sformula1, Sformula2, Tformula, Wformula } from "./formulas";
import { MenuItem, makeStyles, useMediaQuery } from "@material-ui/core";
import React, { useState } from "react";
import { convertLocaleStringToNumber, convertStringInputValueToNumber } from "src/lib/convertLocaleStringToNumber";
import { dropdown2Options, dropdown5Options, dropdown5data } from "./data";
import { onlyNumbersRegexDE, onlyNumbersRegexEN } from "src/components/common/constants";

import { CalculatorButton } from "../components/CalculatorButton";
import { CalculatorButtonLinkCentered } from "../components/CalculatorButtonLinkCentered";
import { CalculatorImage } from "../CalculatorImage";
import CalculatorInputSection from "../CalculatorInputSection";
import { FahrradVvsAutoRechnerTable } from "./table";
import LocaleLink from "src/components/Navigation/LocaleLink";
import { TheCycleverseTheme } from "src/theme";
import useTranslation from "next-translate/useTranslation";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  anchorTag: {
    textDecoration: "none",
    color: "black",
  },

  listLinkContainer: {
    display: "flex",
    flexDirection: "column",
    margin: "35px 0",
  },

  listLinkHeader: {
    color: theme.grayColor,
    fontSize: "24px",
    marginTop: "15px",
    fontFamily: "inherit",
    fontWeight: 300,
    lineHeight: 1.1,
    marginBottom: "5px",
  },

  resultContainer: {
    width: "70%",
    margin: "auto",
    "@media (max-width: 660px)": {
      width: "95%",
    },
  },

  resultTitle: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "16px",
  },

  result: {
    fontWeight: "bolder",
    color: theme.greenColor,
  },

  whiteBackgroundColor: {
    backgroundColor: "#ffffff",
  },

  errorMessage: {
    fontSize: "16px",
    color: theme.productPageTeaserHeadlineColor,
    textAlign: "center",
    width: "75%",
    margin: "auto",
    marginTop: "10px",
    "@media (max-width: 488px)": {
      width: "100%",
    },
  },
}));

type Results = {
  J: number;
  M: number;
  W: number;
  T: number;
  K: number | string;
  G: number;
  B: number | string;
  Nox: number;
  Noxg: number;
  CO: number | string;
  COg: number;
  E: number | string;
  S: number | string;
  MW: number | string;
  P: number;
  h: number;
  PS: number;
  St: number;
  Zs: number;
  kCal: number;
  Gs: number | string;
  field6Result: number;
};

const FahrradVvsAutoRechnerComponent = () => {
  const { t, lang } = useTranslation("fahrradVvsAutoRechner");
  const classes = useStyles();
  const isSmallDevice = useMediaQuery("(max-width: 585px)");

  useState;

  const initialState = {
    field1: null,
    field2: null,
    field3: null,
    field4a: null,
    field4b: null,
    field5: null,
    field6: null,
  };

  const [fields, setFields] = useState(initialState);
  const [results, setResults] = useState<Results>();
  const [error, setError] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, isTypeNumber?: boolean) => {
    setResults(null);
    const { name, value } = e.target;
    if (
      isTypeNumber &&
      ((lang === "en" && !onlyNumbersRegexEN.test(value)) || (lang === "de" && !onlyNumbersRegexDE.test(value)))
    ) {
      return false;
    }
    setFields({ ...fields, [name]: value });
  };

  const generateResults = () => {
    const {
      field1: field1Value,
      field2,
      field3: field3Value,
      field4a: field4aValue,
      field4b: field4bValue,
      field5,
      field6: field6Value,
    } = fields;

    let t: number;
    let C: number;
    let Nox: number;
    let CO: number;
    let S: number;
    let D: number;
    let MW: number;
    let P: number;
    let PS: number;
    let St: number;
    let kCal: number;

    if (
      !field2 ||
      (field2 === "dropdown2Option1" && !(field1Value && field3Value && field4aValue && field6Value)) ||
      (field2 === "dropdown2Option2" && !(field1Value && field3Value && field4aValue && field6Value)) ||
      (field2 === "dropdown2Option3" && !(field1Value && field4bValue && field6Value)) ||
      (field2 === "dropdown2Option4" &&
        !(field1Value && field3Value && field4aValue && field4bValue && field5 && field6Value))
    ) {
      setError(true);
      return;
    } else {
      setError(false);
    }

    // handle field5
    const field1 = convertStringInputValueToNumber(field1Value);
    const field3 = field3Value && convertStringInputValueToNumber(field3Value);
    const field6 = convertStringInputValueToNumber(field6Value);
    const field4a = field4aValue && convertStringInputValueToNumber(field4aValue);
    const field4b = field4bValue && convertStringInputValueToNumber(field4bValue);

    if (field5 === "dropdown5Option1") {
      t = 365;
    } else if (field5 === "dropdown5Option2") {
      t = 30.44;
    } else if (field5 === "dropdown5Option3") {
      t = 7;
    } else if (field5 === "dropdown5Option4") {
      t = 1;
    }

    if (field2 === "dropdown2Option1") {
      if (field3 < 4.5) {
        C = 0.12;
      } else if (field3 >= 4.5 && field3 < 6.5) {
        C = 0.16;
      } else if (field3 >= 6.5) {
        C = 0.23;
      }
    } else if (field2 === "dropdown2Option2") {
      if (field3 < 4.5) {
        C = 0.09;
      } else if (field3 >= 4.5 && field3 < 6.5) {
        C = 0.14;
      } else if (field3 >= 6.5) {
        C = 0.2;
      }
    } else if (field2 === "dropdown2Option3") {
      C = 0.055;
    } else if (field2 === "dropdown2Option4") {
      C = 0.117;
    }

    if (field2 === "dropdown2Option2") {
      Nox = Number(field1) * Number(field6) * t * 0.00008;
    } else if (field2 === "dropdown2Option1") {
      Nox = Number(field1) * Number(field6) * t * 0.00006;
    } else if (field2 === "dropdown2Option4") {
      Nox = Number(field1) * Number(field6) * t * 0.00003;
    }

    if (field2 === "dropdown2Option2" || field2 === "dropdown2Option4") {
      CO = Number(field1) * Number(field6) * t * 0.0005;
    } else if (field2 === "dropdown2Option1") {
      CO = Number(field1) * Number(field6) * t * 0.001;
    }

    if (field2 === "dropdown2Option1" || field2 === "dropdown2Option2") {
      S = Sformula1(Number(field1), Number(field6), Number(field4a), Number(field3), t);
    } else if (field2 === "dropdown2Option3" && field4b) {
      S = Sformula2(Number(field1), Number(field6), Number(field4b), t);
    } else if (field2 === "dropdown2Option4" && field4b) {
      S = Sformula(Number(field1), Number(field6), Number(field4a), Number(field4b), Number(field3), t);
    }

    if (field3 < 4.5) {
      D = 1500;
    } else if (field3 >= 4.5 && field3 < 6.5) {
      D = 1500 * 1.5;
    } else if (field3 >= 6.5) {
      D = 1500 * 2.5;
    }

    // W → Money saved on maintenance
    if (field5 === "dropdown5Option1") {
      MW = Number(field6) * D;
    } else if (field5 === "dropdown5Option2") {
      MW = (Number(field6) * D) / 12;
    } else if (field5 === "dropdown5Option3") {
      MW = (Number(field6) * D) / 52;
    } else if (field5 === "dropdown5Option4") {
      MW = (Number(field6) * D) / 365;
    }

    // P → Cost of parking
    if (field5 === "dropdown5Option1") {
      P = Number(field6) * 826;
    } else if (field5 === "dropdown5Option2") {
      P = (Number(field6) * 826) / 12;
    } else if (field5 === "dropdown5Option3") {
      P = (Number(field6) * 826) / 52;
    } else if (field5 === "dropdown5Option4") {
      P = (Number(field6) * 826) / 365;
    }

    // PS → hours saved looking for a parking spot
    if (field5 === "dropdown5Option1") {
      PS = Number(field6) * 41;
    } else if (field5 === "dropdown5Option2") {
      PS = (Number(field6) * 41) / 12;
    } else if (field5 === "dropdown5Option3") {
      PS = (Number(field6) * 41) / 52;
    } else if (field5 === "dropdown5Option4") {
      PS = (Number(field6) * 41) / 365;
    }

    // St → hours saved waiting in traffic
    if (field5 === "dropdown5Option1") {
      St = Number(field6) * 46;
    } else if (field5 === "dropdown5Option2") {
      St = (Number(field6) * 46) / 12;
    } else if (field5 === "dropdown5Option3") {
      St = (Number(field6) * 46) / 52;
    } else if (field5 === "dropdown5Option4") {
      St = (Number(field6) * 46) / 365;
    }

    const Z = Number(Number(field1) / 15); // 15kmh mit fahrrad im schnitt
    const J = Math.floor(Jformula(Number(Z), Number(field6), t));
    const Mresult = Mformula(Number(Z), Number(field6), t, J);
    const M = J > 0 ? Math.round(Mresult) : J === 0 && Math.floor(Mresult);

    const wResult = Wformula(Number(Z), Number(field6), t, J, M);

    const W = M > 0 ? Math.round(wResult) : M === 0 && Math.floor(wResult);
    const T = Math.round(Tformula(Number(Z), Number(field6), t, J, M, W));

    const kResult = Number(Kformula(field6, t, field1, C).toFixed(2));

    const K = kResult;
    const Noxg = Nox * 1000;
    const COg = Math.round(CO * 1000);
    const E = Number((S + MW + P).toFixed(2));

    const G = Math.round(Number(kResult) * 1000);
    const bResult = Number((kResult / 10).toFixed(1));
    const B = bResult;

    const h = PS + St;

    // Zs → hours of sport (cycling) per day
    const Zs = Number((Number(field1) / 15).toFixed(1));

    // kCal → burnt calories per day
    if (field5 === "dropdown5Option1") {
      kCal = Math.round(Z * 390);
    }

    // G → costs for society
    const Gs = Number((Number(field1) * Number(field6) * 0.5 * t).toFixed(2));

    setResults({
      ...results,
      J,
      M,
      W,
      T,
      K,
      G,
      B,
      CO,
      COg,
      E,
      S,
      MW,
      P,
      h,
      PS,
      St,
      Zs,
      kCal,
      Gs,
      Noxg: Math.round(Noxg),
      field6Result: Number(field6),
    });
  };

  return (
    <>
      <CalculatorImage
        src="/images/calculators/fahrrad-vs-auto-rechner/Fahrrad-vs-Auto-Rechner.svg"
        width={isSmallDevice ? 450 : 750}
        height={isSmallDevice ? 150 : 350}
        layout="intrinsic"
        alt={t("altImage1")}
      />

      <CalculatorButton text={t("scrollDownButton")} scroll idToScrollTo={t("linkAnchor1")} />
      <CalculatorBodyText>{t("paragraph1")}</CalculatorBodyText>

      <CalculatorBodyText>
        {
          <>
            {t("paragraph2")}{" "}
            <LocaleLink as={"/blog/fahrrad-fahren-gesund"} href={"/blog/fahrrad-fahren-gesund"} target="new">
              {t("link1")}
            </LocaleLink>{" "}
            {t("and")}{" "}
            <LocaleLink as={"/blog/99-gruende-radfahren"} href={"/blog/99-gruende-radfahren"} target="new">
              {t("link1b")}
            </LocaleLink>
            .
          </>
        }
      </CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph3")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph4")}</CalculatorBodyText>

      <CalculatorBodyText>
        {
          <>
            {t("paragraph5")}{" "}
            <LocaleLink as={t("linkAnchor3")} href={t("linkAnchor3")} anchor>
              {t("link2")}
            </LocaleLink>
            .
          </>
        }
      </CalculatorBodyText>

      <div className={classes.listLinkContainer}>
        <div className={classes.listLinkHeader}>{t("listTitle")}</div>
        <ListLinkText link={`#${t("linkAnchor1")}`} linkName={t("listItem1")} />
        <ListLinkText link={`#${t("linkAnchor2")}`} linkName={t("listItem2")} />
        <ListLinkText link={`#${t("linkAnchor3")}`} linkName={t("listItem3")} />
      </div>

      <div
        id={t("linkAnchor1")}
        style={{ textAlign: "center", marginTop: "50px" }}
        data-test="page-rechner-fahrrad-vs-auto-rechner"
      >
        <CalculatorHeadline>{t("calculatorHeadline")}</CalculatorHeadline>
      </div>

      <CalculatorInputSection
        inputTitle={t("input1Title")}
        inputNumber="1"
        inputCaption={t("input1Caption")}
        inputUnite={"km"}
        name="field1"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
        value={fields?.field1}
      />

      <CalculatorInputSection
        inputTitle={t("input2Title")}
        inputNumber="2"
        isSelect
        name="field2"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, false)}
        value={fields?.field2}
      >
        {dropdown2Options?.map((el) => (
          <MenuItem key={el} value={el}>
            {t(el)}
          </MenuItem>
        ))}
      </CalculatorInputSection>

      {fields?.field2 !== "dropdown2Option3" ? (
        <CalculatorInputSection
          inputTitle={t("input3Title")}
          inputNumber="3"
          inputUnite={"Liter/100km"}
          name="field3"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
          value={fields?.field3}
        />
      ) : null}

      {fields?.field2 !== "dropdown2Option3" ? (
        <CalculatorInputSection
          inputTitle={t("input4Titlea")}
          inputNumber={"4"}
          inputUnite={"Euro/Liter"}
          name="field4a"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
          value={fields?.field4a}
        />
      ) : null}

      {fields?.field2 === "dropdown2Option3" || fields?.field2 === "dropdown2Option4" ? (
        <CalculatorInputSection
          inputTitle={t("input4Titleb")}
          inputNumber={fields?.field2 === "dropdown2Option3" ? "3" : "5"}
          inputUnite={"Euro/kWh"}
          name="field4b"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
          value={fields?.field4b}
        />
      ) : null}

      <>
        <CalculatorInputSection
          inputTitle={t("input5Title")}
          inputNumber={
            fields?.field2 === "dropdown2Option3"
              ? "4"
              : fields?.field2 === "dropdown2Option1" || fields?.field2 === "dropdown2Option2"
              ? "5"
              : "6"
          }
          isSelect
          name="field5"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, false)}
          value={fields?.field5}
        >
          {dropdown5Options?.map((el) => (
            <MenuItem key={el} value={el}>
              {t(el)}
            </MenuItem>
          ))}
        </CalculatorInputSection>

        {fields?.field5 ? (
          <div style={{ marginTop: "30px" }}>
            <CalculatorInputSection
              isNotSection
              inputUnite={Number(fields?.field6) === 1 ? t(dropdown5data[fields?.field5]) : t(fields?.field5)}
              name="field6"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
              value={fields?.field6}
            />
          </div>
        ) : null}
      </>

      <CalculatorButton text={t("resultButton")} onClick={generateResults} />

      {error ? <p className={classes.errorMessage}>{t("errorMessage")}</p> : null}

      {results?.field6Result ? <div className={classes.resultTitle}>{t("resultTitle")}</div> : null}
      {results?.field6Result ? (
        <div style={{ textAlign: "center" }}>
          <CalculatorInputHeadline>
            <span>{t("resultText1a")}</span>
            <span className={classes.result}>
              {" "}
              {` ${fields?.field6} `}
              {Number(fields?.field6) === 1 ? t(dropdown5data[fields?.field5]) : t(fields?.field5)}{" "}
            </span>
            <span>{t("resultText1b")}</span>
          </CalculatorInputHeadline>
        </div>
      ) : null}

      <div className={classes.resultContainer}>
        {results?.J > 0 ? (
          <DisplayResult
            texta={t("result2Text")}
            resulta={
              results?.J > 1
                ? `${results?.J?.toLocaleString(lang)} ${t("dropdown5Option1")}`
                : `${results?.J?.toLocaleString(lang)} ${t(dropdown5data["dropdown5Option1"])}`
            }
            resultb={
              results?.M > 1
                ? `${results?.M?.toLocaleString(lang)} ${t("dropdown5Option2")}`
                : results?.M
                ? `${results?.M?.toLocaleString(lang)} ${t(dropdown5data["dropdown5Option2"])}`
                : null
            }
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.J <= 0 && results?.M > 0 ? (
          <DisplayResult
            texta={t("result2Text")}
            resulta={
              results?.M > 1
                ? `${results?.M?.toLocaleString(lang)} ${t("dropdown5Option2")}`
                : `${results?.M?.toLocaleString(lang)} ${t(dropdown5data["dropdown5Option2"])}`
            }
            resultb={
              results?.W > 1
                ? `${results?.W?.toLocaleString(lang)} ${t("dropdown5Option3")}`
                : results?.W
                ? `${results?.W?.toLocaleString(lang)} ${t(dropdown5data["dropdown5Option3"])}`
                : null
            }
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.M <= 0 && results?.W > 0 ? (
          <DisplayResult
            texta={t("result2Text")}
            resulta={
              results?.W > 1
                ? `${results?.W?.toLocaleString(lang)} ${t("dropdown5Option3")}`
                : `${results?.W?.toLocaleString(lang)} ${t(dropdown5data["dropdown5Option3"])}`
            }
            resultb={
              results?.T > 1
                ? `${results?.T?.toLocaleString(lang)} ${t("dropdown5Option4")}`
                : results?.T
                ? `${results?.T?.toLocaleString(lang)} ${t(dropdown5data["dropdown5Option4"])}`
                : null
            }
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.W <= 0 && results?.T > 0 ? (
          <DisplayResult
            texta={t("result2Text")}
            resulta={
              results?.T > 1
                ? `${results?.T?.toLocaleString(lang)} ${t("dropdown5Option1")}`
                : `${results?.T?.toLocaleString(lang)} ${t(dropdown5data["dropdown5Option1"])}`
            }
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {/* ====================== */}

        {results?.K || results?.K === 0 || results?.G || results?.G === 0 ? (
          <>
            {results?.K >= 1 ? (
              <DisplayResult
                texta={t("result3Text")}
                resulta={`${results?.K?.toLocaleString(lang, { maximumFractionDigits: 2 })} ${t("kg")}`}
                backgroundColor={classes.whiteBackgroundColor}
              />
            ) : (
              <DisplayResult
                texta={t("result3Text")}
                resulta={`${results?.G?.toLocaleString(lang, { maximumFractionDigits: 0 })} ${t("gramm")}`}
                backgroundColor={classes.whiteBackgroundColor}
              />
            )}
          </>
        ) : null}

        {results?.B || results?.B === 0 ? (
          <DisplayResultWithTwoText
            texta={t("resultText3a")}
            resulta={`${results?.B ? Math.round(Number(results.B)).toLocaleString(lang) : null}`}
            textb={t("resultText3b")}
          />
        ) : null}

        {fields?.field2 !== "dropdown2Option3" ? (
          <>
            {results?.Nox >= 1 ? (
              <DisplayResult
                texta={t("result4Text")}
                resulta={`${results?.Nox?.toLocaleString(lang, { maximumFractionDigits: 2 })} ${t("kg")}`}
                backgroundColor={classes.whiteBackgroundColor}
              />
            ) : results?.Noxg || results?.Noxg === 0 ? (
              <DisplayResult
                texta={t("result4Text")}
                resulta={`${results?.Noxg?.toLocaleString(lang, { maximumFractionDigits: 0 })} ${t("gramm")}`}
                backgroundColor={classes.whiteBackgroundColor}
              />
            ) : null}
          </>
        ) : null}

        {fields?.field2 !== "dropdown2Option3" ? (
          <>
            {results?.CO >= 1 ? (
              <DisplayResult
                texta={t("result5Text")}
                resulta={`${results?.CO.toLocaleString(lang, { maximumFractionDigits: 2 })} ${t("kg")}`}
                backgroundColor={classes.whiteBackgroundColor}
              />
            ) : results?.COg || results?.COg === 0 ? (
              <DisplayResult
                texta={t("result5Text")}
                resulta={`${results?.COg?.toLocaleString(lang, { maximumFractionDigits: 0 })} ${t("gramm")}`}
                backgroundColor={classes.whiteBackgroundColor}
              />
            ) : null}
          </>
        ) : null}

        {results?.E || results?.E === 0 ? (
          <DisplayResultWithTwoText
            texta={t("result6Text")}
            resulta={`${Math.round(Number(results?.E))?.toLocaleString(lang)}`}
            textb={`${t("euro")}:`}
          />
        ) : null}

        {results?.S || results?.S === 0 ? (
          <DisplayResultFirst
            resulta={`${Math.round(Number(results?.S)).toLocaleString(lang)}`}
            texta={
              fields?.field2 !== "dropdown2Option3"
                ? t("result7Texta")
                : fields?.field2 === "dropdown2Option4"
                ? t("result10Text")
                : t("result7Textb")
            }
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.MW || results?.MW === 0 ? (
          <DisplayResultFirst
            resulta={`${Math.round(Number(results?.MW))?.toLocaleString(lang)}`}
            texta={t("result8Text")}
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.P || results?.P === 0 ? (
          <DisplayResultFirst
            resulta={`${Math.round(results?.P)?.toLocaleString(lang)}`}
            texta={t("result9Text")}
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.h || results?.h === 0 ? (
          <DisplayResultWithTwoText
            texta={t("result11Texta")}
            resulta={`${results?.h?.toLocaleString(lang)}`}
            textb={t("result11Textb")}
          />
        ) : null}

        {results?.PS ? (
          <DisplayResultWithTwoText
            texta={t("result12Texta")}
            resulta={`${results?.PS?.toLocaleString(lang)}`}
            textb={t("result12Textb")}
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.St ? (
          <DisplayResultWithTwoText
            texta={t("result12Texta")}
            resulta={`${results?.St?.toLocaleString(lang)}`}
            textb={t("result13Textb")}
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.Zs ? (
          <DisplayResultWithTwoText
            texta={t("result14Texta")}
            resulta={`${(results?.Zs ? Math.round(results.Zs * 60) : null)?.toLocaleString(lang)}`}
            textb={t("result14Textb")}
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.kCal ? (
          <DisplayResultWithTwoText
            texta={t("result15Texta")}
            resulta={`${results?.kCal?.toLocaleString(lang)}`}
            textb={t("result15Textb")}
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}

        {results?.Gs ? (
          <DisplayResultWithTwoText
            texta={t("result16Texta")}
            resulta={`${Math.round(Number(results?.Gs))?.toLocaleString(lang)}`}
            textb={t("result16Textb")}
            backgroundColor={classes.whiteBackgroundColor}
          />
        ) : null}
      </div>

      <div id={t("linkAnchor2")}>
        <H3Headline>{t("paragraph6Title")}</H3Headline>
      </div>

      <CalculatorBodyText>{t("paragraph6")}</CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph8")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph9")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph10")}</CalculatorBodyText>

      <CalculatorButtonLinkCentered text={t("button2")} href="/blog/verkehrswende-statistiken" />

      <H3Headline>{t("paragraph11")}</H3Headline>
      <div style={{ marginTop: "15px" }}>
        <CalculatorImage
          src="/images/calculators/fahrrad-vs-auto-rechner/PlusLeben.svg"
          width={450}
          height={150}
          layout="intrinsic"
          alt={t("altImage2")}
        />
      </div>

      <CalculatorBodyText>
        <span>{t("paragraph12a")} </span>
        <LocaleLink
          as={"https://www.uu.nl/en/news/dutch-bikers-live-six-months-longer"}
          href={"https://www.uu.nl/en/news/dutch-bikers-live-six-months-longer"}
          target="new"
          externalLink
          dofollow
        >
          {t("link3")}
        </LocaleLink>{" "}
        <span>{t("paragraph12b")} </span>
      </CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph13")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph14")}</CalculatorBodyText>

      <H3Headline>{t("paragraph14Title")}</H3Headline>
      <CalculatorBodyText>
        <span>{t("paragraph14a")}</span>{" "}
        <LocaleLink
          as={"https://www.alternativ-mobil.info/neuwagen-finden/verbrauchswerte-sind-wichtig"}
          href={"https://www.alternativ-mobil.info/neuwagen-finden/verbrauchswerte-sind-wichtig"}
          target="new"
          externalLink
          dofollow
        >
          {t("link4")}
        </LocaleLink>{" "}
        <span>{t("paragraph14b")}</span>
      </CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph15")}</CalculatorBodyText>
      <CalculatorBodyText>
        <span>{t("paragraph16")}</span>{" "}
        <LocaleLink
          as={
            "https://www.enbw.com/blog/elektromobilitaet/co2-bilanz-wie-umweltfreundlich-sind-elektroautos/#:~:text=Die%20CO2%2DBilanz%20eines%20Elektroautos,anderen%20regelm%C3%A4%C3%9Fig%20aufgeladen%20werden%20muss."
          }
          href={
            "https://www.enbw.com/blog/elektromobilitaet/co2-bilanz-wie-umweltfreundlich-sind-elektroautos/#:~:text=Die%20CO2%2DBilanz%20eines%20Elektroautos,anderen%20regelm%C3%A4%C3%9Fig%20aufgeladen%20werden%20muss."
          }
          target="new"
          externalLink
        >
          {t("link5")}1
        </LocaleLink>{" "}
        {", "}
        <LocaleLink
          as={"https://www.autobild.de/artikel/plug-in-hybride-co2-ausstoss-mehr-als-doppelt-so-hoch--18382295.html"}
          href={"https://www.autobild.de/artikel/plug-in-hybride-co2-ausstoss-mehr-als-doppelt-so-hoch--18382295.html"}
          target="new"
          externalLink
        >
          {t("link5")}2
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <H3Headline>{t("paragraph17Title")}</H3Headline>
      <CalculatorBodyText>
        <span>{t("paragraph17")}</span>{" "}
        <LocaleLink
          as={"https://a.plant-for-the-planet.org/wp-content/uploads/2020/12/faktenblatt_baeume_co2.pdf"}
          href={"https://a.plant-for-the-planet.org/wp-content/uploads/2020/12/faktenblatt_baeume_co2.pdf"}
          target="new"
          externalLink
        >
          {t("link5")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <H3Headline>{t("paragraph18Title")}</H3Headline>
      <CalculatorBodyText>{t("paragraph18")}</CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph19")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph20")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph21")}</CalculatorBodyText>

      <CalculatorBodyText>
        <span>{t("paragraph22")}</span>{" "}
        <LocaleLink
          as={"https://www.bussgeldkatalog.org/euro-6-grenzwerte/"}
          href={"https://www.bussgeldkatalog.org/euro-6-grenzwerte/"}
          target="new"
          externalLink
        >
          {t("link5")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <H3Headline>{t("paragraph23Title")}</H3Headline>
      <CalculatorBodyText>{t("paragraph23")}</CalculatorBodyText>

      <CalculatorBodyText>
        <span>{t("paragraph24")}</span>{" "}
        <LocaleLink
          as={"https://www.bussgeldkatalog.org/euro-6-grenzwerte/"}
          href={"https://www.bussgeldkatalog.org/euro-6-grenzwerte/"}
          target="new"
          externalLink
        >
          {t("link5")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <H3Headline>{t("paragraph25Title")}</H3Headline>
      <div style={{ marginTop: "15px" }}>
        <CalculatorImage
          src="/images/calculators/fahrrad-vs-auto-rechner/Sprit.svg"
          width={450}
          height={150}
          layout="intrinsic"
          alt={t("altImage2")}
        />
      </div>
      <CalculatorBodyText>{t("paragraph25")}</CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph26")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph27")}</CalculatorBodyText>
      <CalculatorBodyText>
        <span>{t("paragraph28")}</span>{" "}
        <LocaleLink
          as={
            "https://www.dkv-mobility.com/de/elektromobilitaet/ratgeber/kosten-elektroauto-pro-100km/#:~:text=Stromverbrauch%20beim%20Elektroauto&text=Grunds%C3%A4tzlich%20wird%20der%20durchschnittliche%20Verbrauch,kWh%20Strom%20auf%20100%20km."
          }
          href={
            "https://www.dkv-mobility.com/de/elektromobilitaet/ratgeber/kosten-elektroauto-pro-100km/#:~:text=Stromverbrauch%20beim%20Elektroauto&text=Grunds%C3%A4tzlich%20wird%20der%20durchschnittliche%20Verbrauch,kWh%20Strom%20auf%20100%20km."
          }
          target="new"
          externalLink
        >
          {t("link5")}1
        </LocaleLink>{" "}
        {", "}
        <LocaleLink
          as={
            "https://www.net4energy.com/de-de/mobilitaet/hybrid-auto-verbrauch#:~:text=Kein%20Wunder%20also%2C%20dass%20die,die%20Energiepreise%20steigen%20seit%20Jahren."
          }
          href={
            "https://www.net4energy.com/de-de/mobilitaet/hybrid-auto-verbrauch#:~:text=Kein%20Wunder%20also%2C%20dass%20die,die%20Energiepreise%20steigen%20seit%20Jahren."
          }
          target="new"
          externalLink
        >
          {t("link5")}2
        </LocaleLink>
      </CalculatorBodyText>

      <H3Headline>{t("paragraph29Title")}</H3Headline>
      <CalculatorBodyText>{t("paragraph29")}</CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph30")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph31")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph32")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph32b")}</CalculatorBodyText>

      <CalculatorBodyText>
        <span>{t("paragraph33")}</span>{" "}
        <LocaleLink
          as={"https://www.allianzdirect.de/kfz-versicherung/unterhaltskosten-auto-ratgeber/"}
          href={"https://www.allianzdirect.de/kfz-versicherung/unterhaltskosten-auto-ratgeber/"}
          target="new"
          externalLink
        >
          {t("link5")}1
        </LocaleLink>
        {", "}
        <LocaleLink
          as={
            "https://www.t-online.de/auto/technik/id_84482754/auto-unterhaltskosten-so-viel-kostet-ihr-fahrzeug-pro-monat.html"
          }
          href={
            "https://www.t-online.de/auto/technik/id_84482754/auto-unterhaltskosten-so-viel-kostet-ihr-fahrzeug-pro-monat.html"
          }
          target="new"
          externalLink
        >
          {t("link5")}2
        </LocaleLink>
        .
      </CalculatorBodyText>

      <H3Headline>{t("paragraph34Title")}</H3Headline>
      <div style={{ marginTop: "15px" }}>
        <CalculatorImage
          src="/images/calculators/fahrrad-vs-auto-rechner/Parkgebuehren.svg"
          width={450}
          height={150}
          layout="intrinsic"
          alt={t("altImage3")}
        />
      </div>
      <CalculatorBodyText>{t("paragraph34")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph35")}</CalculatorBodyText>

      <CalculatorBodyText>
        <span>{t("paragraph36")}</span>{" "}
        <LocaleLink
          as={
            "https://inrix.com/press-releases/inrix-studie-zu-den-kosten-des-autofahrens-parken-verursacht-die-hochsten-laufenden-ausgaben/"
          }
          href={
            "https://inrix.com/press-releases/inrix-studie-zu-den-kosten-des-autofahrens-parken-verursacht-die-hochsten-laufenden-ausgaben/"
          }
          target="new"
          externalLink
          dofollow
        >
          {t("link5")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <H3Headline>{t("paragraph37Title")}</H3Headline>
      <div style={{ marginTop: "15px" }}>
        <CalculatorImage
          src="/images/calculators/fahrrad-vs-auto-rechner/Parkverbot.svg"
          width={450}
          height={150}
          layout="intrinsic"
          alt={t("altImage4")}
        />
      </div>
      <CalculatorBodyText>{t("paragraph37")}</CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph38")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph39")}</CalculatorBodyText>

      <CalculatorBodyText>
        <span>{t("paragraph40")}</span>{" "}
        <LocaleLink
          as={
            "https://www.focus.de/auto/news/41-stunden-jaehrlich-suchen-deutsche-durchschnittlich-einen-parkplatz_id_9633099.html"
          }
          href={
            "https://www.focus.de/auto/news/41-stunden-jaehrlich-suchen-deutsche-durchschnittlich-einen-parkplatz_id_9633099.html"
          }
          target="new"
          externalLink
        >
          {t("link5")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <H3Headline>{t("paragraph41Title")}</H3Headline>
      <div style={{ marginTop: "30px", marginBottom: "30px" }}>
        <CalculatorImage
          src="/images/calculators/fahrrad-vs-auto-rechner/Stau.svg"
          width={isSmallDevice ? 450 : 750}
          height={isSmallDevice ? 150 : 350}
          layout="intrinsic"
          alt={t("altImage5")}
        />
      </div>

      <CalculatorBodyText>{t("paragraph41")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph41b")}</CalculatorBodyText>
      <CalculatorBodyText>
        <span>{t("paragraph42")}</span>{" "}
        <LocaleLink
          as={
            "https://www.spiegel.de/auto/pendlerinnen-und-pendler-stehen-im-schnitt-40-stunden-pro-jahr-im-stau-a-df4452dd-8c66-48f7-a140-0046aa637ba7"
          }
          href={
            "https://www.spiegel.de/auto/pendlerinnen-und-pendler-stehen-im-schnitt-40-stunden-pro-jahr-im-stau-a-df4452dd-8c66-48f7-a140-0046aa637ba7"
          }
          target="new"
          externalLink
        >
          {t("link5")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <H3Headline>{t("paragraph43Title")}</H3Headline>
      <CalculatorBodyText>{t("paragraph43")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph44")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph45")}</CalculatorBodyText>

      <H3Headline>{t("paragraph46Title")}</H3Headline>
      <CalculatorBodyText>{t("paragraph46")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph47")}</CalculatorBodyText>

      <CalculatorBodyText>
        <span>{t("paragraph48a")}</span>{" "}
        <LocaleLink as={"/rechner/kalorienverbrauch-rechner"} href={"/rechner/kalorienverbrauch-rechner"} target="new">
          {t("link6")}
        </LocaleLink>{" "}
        <span>{t("paragraph48b")}</span>
      </CalculatorBodyText>

      <CalculatorBodyText>
        <span>{t("paragraph49a")}</span>{" "}
        <LocaleLink as={"/rechner/fahrrad-wattrechner"} href={"/rechner/fahrrad-wattrechner"} target="new">
          {t("link7")}
        </LocaleLink>{" "}
        <span>{t("paragraph49b")}</span>
      </CalculatorBodyText>

      <H3Headline>{t("paragraph50Title")}</H3Headline>
      <CalculatorBodyText>{t("paragraph50")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph51")}</CalculatorBodyText>
      <CalculatorBodyText>
        <span>{t("paragraph52")}</span>{" "}
        <LocaleLink
          as={"https://vivavelo.org/wp-content/uploads/2022/09/Goessling_CBA-Auto-Fahrrad_270418.pdf"}
          href={"https://vivavelo.org/wp-content/uploads/2022/09/Goessling_CBA-Auto-Fahrrad_270418.pdf"}
          target="new"
          externalLink
          dofollow
        >
          {t("link5")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph53")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph54")}</CalculatorBodyText>

      <div id={`${t("linkAnchor3")}`}>
        <H3Headline>{t("paragraph55Title")}</H3Headline>
      </div>
      <CalculatorBodyText>{t("paragraph55")}</CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph56")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph57")}</CalculatorBodyText>

      <FahrradVvsAutoRechnerTable />

      <CalculatorBodyText>
        <span>{t("tableDescription")}</span>{" "}
        <LocaleLink
          as={"https://vivavelo.org/wp-content/uploads/2022/09/Goessling_CBA-Auto-Fahrrad_270418.pdf"}
          href={"https://vivavelo.org/wp-content/uploads/2022/09/Goessling_CBA-Auto-Fahrrad_270418.pdf"}
          target="new"
          externalLink
          dofollow
        >
          {t("link5")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <CalculatorBodyText>{t("paragraph58")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph59")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph60")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph61")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph62")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph63")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph64")}</CalculatorBodyText>

      <CalculatorButtonLinkCentered text={t("button1")} href="/blog/critical-mass-termine" />

      <CalculatorBodyText>{t("paragraph65")}</CalculatorBodyText>

      <CalculatorButtonLinkCentered text={t("button3")} href="/" />
    </>
  );
};

export default FahrradVvsAutoRechnerComponent;
